// ============================================================
// Firebase init — SDK v10 modular, tree-shakeable.
// Owns: app, auth, db. Everything else imports from here.
// ============================================================

import { initializeApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator
} from 'firebase/firestore';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  connectAuthEmulator
} from 'firebase/auth';
import {
  getStorage,
  connectStorageEmulator
} from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator
} from 'firebase/functions';

const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID
};

// Fail loudly if env vars missing — easier to debug than "undefined project".
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('Firebase config missing. Copy .env.example → .env and fill in values from Firebase Console → Project Settings → Your apps (web).');
  throw new Error('VITE_FIREBASE_* env vars missing');
}

export const app = initializeApp(firebaseConfig);

// Firestore with offline IndexedDB cache + multi-tab coordination.
// MUST be initialized before any getFirestore(app) call elsewhere.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});

export const auth = getAuth(app);
// Cloud Storage — used for supplier QR + namecards (replaces V1 Drive uploads).
// Bucket comes from VITE_FIREBASE_STORAGE_BUCKET (.env).
export const storage = getStorage(app);
// Cloud Functions client — region pinned to match the deployed functions
// (asia-southeast1). Used for callable RPCs like createStaffLogin.
export const functions = getFunctions(app, 'asia-southeast1');
export const googleProvider = new GoogleAuthProvider();
// Force account chooser even if user already logged into Google.
googleProvider.setCustomParameters({ prompt: 'select_account' });

// Emulator wiring — active only when running on localhost AND
// VITE_USE_EMULATORS=1 in .env. Keeps prod-from-localhost viable.
if (
  location.hostname === 'localhost' &&
  import.meta.env.VITE_USE_EMULATORS === '1'
) {
  console.info('[firebase] Using local emulators (auth:9099, firestore:8080, storage:9199, functions:5001)');
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// ── auth helpers ─────────────────────────────────────────────

export async function loginWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

/**
 * Username/password login (Phase 5C). Maps a bare username + tenantId to
 * the synthetic email pattern used by the staff-login auth pool, then
 * signs in via Firebase Email/Password.
 *
 *   loginWithUsername('ruzaidi', '0987654321', 'ccy-engineering')
 *   → signs in with email 'ruzaidi@ccy-engineering.tenant.local'
 *
 * tenantId is passed explicitly because pre-auth we have no user context
 * to resolve it from. main.js supplies the default tenantId from
 * platform/tenant.js DEFAULT_TENANT_ID until subdomain routing lands.
 */
export async function loginWithUsername(username, password, tenantId) {
  const u = String(username || '').trim().toLowerCase();
  const t = String(tenantId  || '').trim().toLowerCase();
  if (!u || !password || !t) {
    throw new Error('Username, password and tenantId are required');
  }
  if (!/^[a-z0-9_.-]+$/.test(u)) {
    throw new Error('Username may only contain letters, digits, underscore, dot or dash');
  }
  const email = `${u}@${t}.tenant.local`;
  const cred  = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

/**
 * Self-service password change for username-auth users — used by the
 * Force-Change-Password screen on first login (and by a future "Change
 * my password" screen in My Profile).
 *
 * The caller must already be signed in with username auth. After the
 * Auth password is updated, the /users/{uid} doc's forcePasswordChange
 * flag is flipped to false (rule allows the user to do this themselves
 * provided no other field changes).
 */
export async function changeOwnPassword(newPassword) {
  if (!auth.currentUser) throw new Error('Not signed in');
  if (!newPassword || newPassword.length < 8) {
    throw new Error('Password must be at least 8 characters');
  }
  await updatePassword(auth.currentUser, newPassword);
  // Mirror flag on the user doc — lazy import to avoid circular dep.
  try {
    const { clearForcePasswordChange } = await import('./users.js');
    await clearForcePasswordChange(auth.currentUser.uid);
  } catch (e) { /* the flag is best-effort; Auth has the truth */ }
  // Force token refresh so subsequent API calls pick up the
  // (possibly soon-to-be-cleared) custom claim.
  try { await auth.currentUser.getIdToken(true); } catch (e) {}
}

export async function logout() {
  await signOut(auth);
}

export function onAuth(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── role helper (reads claim, falls back to /users/{uid} doc) ───────────

/**
 * Returns the user's role with priority:
 *   1. Firebase Auth custom claim  (set via `npm run set-role`)
 *   2. /users/{uid}.role doc field (Spark plan compatible — written by
 *      the Admin > Settings UI; mirrors the Firestore Rules role() helper)
 *   3. fallback 'staff' for any CCY user (so first-time sign-in works)
 */
export async function getUserRole(user) {
  if (!user) return null;
  let tokenResult = await user.getIdTokenResult(/* forceRefresh */ false);
  let claimRole = tokenResult.claims.role;
  if (claimRole) return claimRole;

  // ── 2026-09-22 — ONE targeted refresh on the STALENESS SIGNATURE ──
  //
  // The precedence above is correct and unchanged: the claim wins, exactly
  // as firestore.rules role() does. The failure this guards is different.
  //
  // getIdTokenResult(false) returns the CACHED ID token. A claim written by
  // setCustomUserClaims does NOT appear in a client's token until that token
  // is next refreshed. So between the claim being set and the next refresh,
  // `claims.role` is absent here while the SERVER already evaluates the new
  // claim — the client renders one role and Firestore enforces another.
  //
  // WHY THIS SIGNATURE AND NOT "claim disagrees with document":
  // a claim/document DISAGREEMENT is a normal, permanent state in this
  // system — an account can hold claim `director` while its /users document
  // still says `staff`, and the document is never rewritten to match.
  // Refreshing on disagreement would force a network round-trip on EVERY
  // boot for those accounts, forever, and never resolve anything. Absence of
  // the claim is the only signature that a refresh can actually fix.
  //
  // WHY NOT getIdTokenResult(true) UNCONDITIONALLY:
  // that adds a round-trip to every cold load for every user, on a path that
  // already blocks boot, to cover a window that closes on its own within the
  // token lifetime. Bad trade.
  //
  // FRESH-TOKEN GUARD: a token minted seconds ago already carries current
  // claims, so if the claim is absent there it is genuinely absent and a
  // refresh cannot help. Skipping that case keeps the normal sign-in path at
  // zero extra round-trips; only a RESUMED session with no claim pays one.
  try {
    const issuedMs = Date.parse(tokenResult.issuedAtTime || '');
    const tokenAgeMs = Number.isFinite(issuedMs) ? (Date.now() - issuedMs) : Infinity;
    if (tokenAgeMs > 120000) {
      tokenResult = await user.getIdTokenResult(/* forceRefresh */ true);
      claimRole = tokenResult.claims.role;
      if (claimRole) return claimRole;
    }
  } catch (e) {
    // Offline or a transient refresh failure — fall through to the document.
    // Never let this throw: it would block boot for a user who has a
    // perfectly good document role.
  }

  // Doc fallback — lazy import to avoid circular dep with users.js
  try {
    const { readUserRole } = await import('./users.js');
    const docRole = await readUserRole(user.uid);
    if (docRole) return docRole;
  } catch (e) { /* default to staff below */ }

  if (user.email && user.email.endsWith('@ccyengineering.com')) return 'staff';
  // Synthetic username-auth users default to 'staff' too — claims/doc are
  // expected to land via createStaffLogin Cloud Function, but a stale
  // token between that call and the next refresh shouldn't lock them out.
  if (user.email && user.email.endsWith('.tenant.local')) return 'staff';
  return null;
}

// ── tenant helper — re-exported from platform/tenant.js for convenience ─
// Kept here so callers don't need to know about the platform/ split, and
// for symmetry with getUserRole. Adding the function here also lets us
// keep a single import surface in main.js.
export { getUserTenant, DEFAULT_TENANT_ID } from './platform/tenant.js';

export function canManageInventory(role) {
  return role === 'director' || role === 'admin';
}
export function canSeeHR(role) {
  return role === 'director' || role === 'hr';
}
